// Ret-3 "Submit retention to manufacturer": the doctor's Ret-1 retainer choice is
// pre-filled and editable here; the forward edge is gated on an order being set.

import { useCallback, useEffect, useState } from 'react';
import { CubeTransparentIcon } from '@heroicons/react/24/outline';
import { RetainerSelector, retainerOptionFromOrderValue } from '@odo/shared-ui';
import type { RetainerOptionId } from '@odo/shared-ui';
import type { TaskBodyProps } from './index';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { FilesTabContent } from '../../patient-files/FilesTabContent';
import { setRetainerOrder } from '../../../services/retention';
import { useToast } from '../../../providers/ToastProvider';
import { safeHref } from '../../../utils/safeHref';

export function RetentionSubmitManufacturingTask({ workCase: c, onPayloadChange }: TaskBodyProps) {
    const { data, refetch } = usePatientCard(c.id);
    const { showToast } = useToast();
    const manufacturer = data?.treatment?.manufacturer ?? null;
    const manufacturerHref = safeHref(data?.treatment?.manufacturerLink);
    const order = data?.status?.orders ?? null;

    const [selected, setSelected] = useState<RetainerOptionId | null>(null);
    const [saving, setSaving] = useState(false);

    // The stored order is the source of truth; local state only leads it while a save is in flight.
    useEffect(() => {
        setSelected(retainerOptionFromOrderValue(order));
    }, [order]);

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: selected !== null });
    }, [onPayloadChange, selected]);

    const handleSelect = useCallback(
        (option: RetainerOptionId): void => {
            const previous = selected;
            setSelected(option);
            setSaving(true);
            setRetainerOrder(c.id, option)
                .then(() => refetch())
                .then(() => showToast('Retainer order updated', 'success'))
                .catch((err: unknown) => {
                    setSelected(previous);
                    const message =
                        err instanceof Error ? err.message : 'Failed to update the retainer order';
                    showToast(message, 'error');
                })
                .finally(() => setSaving(false));
        },
        [c.id, selected, refetch, showToast]
    );

    return (
        <div className="flex-1 min-h-0 flex flex-col px-4 pt-3 pb-3 gap-3 font-inter">
            <div className="flex items-center gap-3 px-5 py-4 bg-blue-50 border border-blue-100 rounded-xl flex-shrink-0">
                <CubeTransparentIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900">Submit the retainer order</div>
                    <div className="text-xs text-gray-600 mt-0.5">
                        Place the retainer order with the manufacturer using the order details and
                        approved records below, then confirm with the action below to advance the
                        case to manufacturing.
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex-shrink-0">
                <div className="text-[11px] font-medium text-gray-500 mb-0.5">Manufacturer</div>
                {manufacturer ? (
                    manufacturerHref ? (
                        <a
                            href={manufacturerHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-blue-600 hover:underline break-words"
                        >
                            {manufacturer}
                        </a>
                    ) : (
                        <span className="text-sm font-semibold text-gray-800 break-words">
                            {manufacturer}
                        </span>
                    )
                ) : (
                    <span className="text-sm text-gray-400 italic">Not set</span>
                )}
            </div>

            {/* The doctor's retainer choice — pre-filled, editable, saved on select. */}
            <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex-shrink-0 space-y-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-900">Retainer order</span>
                    {saving && <span className="text-[11px] text-gray-500">Saving…</span>}
                    {!saving && selected === null && (
                        <span className="text-[11px] text-amber-600 font-medium">
                            Not set — pick the order the doctor requested to continue
                        </span>
                    )}
                </div>
                {/* includeOutsideOdo={false} keeps outside_odo out, so only orderable ids arrive. */}
                <RetainerSelector
                    layout="grid"
                    selectedRetainer={selected}
                    onSelect={(id) => handleSelect(id as RetainerOptionId)}
                    includeOutsideOdo={false}
                    disabled={saving}
                />
            </div>

            {/* Approved retention records for reference while placing the order. */}
            <div className="flex-1 min-h-0 flex flex-col">
                <FilesTabContent patientId={c.id} />
            </div>
        </div>
    );
}
